import { useMemo, useState } from "react";
import {
  AreaChart,
  BarChart,
  ChartTooltip,
  DonutChart,
  FunnelChart,
} from "@mantine/charts";
import {
  Badge,
  Box,
  Group,
  SegmentedControl,
  Stack,
  Text,
  Title,
  UnstyledButton,
} from "@mantine/core";
import { IconChevronDown } from "@tabler/icons-react";
import dayjs from "dayjs";

import { EmptyState, hasChartData, usePartShown } from "./SectionShell";
import "./charts.css";

export const BUCKET_OPTIONS = [
  { value: "1", label: "1h" },
  { value: "2", label: "2h" },
  { value: "3", label: "3h" },
  { value: "6", label: "6h" },
  { value: "12", label: "12h" },
  { value: "24", label: "Day" },
];

const bucketStart = (hour, hoursPerBucket) => {
  const stepMs = hoursPerBucket * 3600_000;
  return Math.floor(dayjs(hour).valueOf() / stepMs) * stepMs;
};

const groupByHours = (rows = [], hoursPerBucket) => {
  if (hoursPerBucket <= 1) return rows;
  const out = new Map();
  for (const row of rows) {
    const key = bucketStart(row.hour, hoursPerBucket);
    if (Number.isNaN(key)) continue;
    const entry = out.get(key) ?? { hour: key };
    for (const [k, v] of Object.entries(row)) {
      if (k !== "hour") entry[k] = (entry[k] ?? 0) + (Number(v) || 0);
    }
    out.set(key, entry);
  }
  return [...out.values()].sort((a, b) => a.hour - b.hour);
};

const formatBucket = (bucket, hoursPerBucket) =>
  hoursPerBucket >= 24
    ? dayjs(bucket).format("MMM D")
    : dayjs(bucket).format("MMM D HH:mm");

const PIE_PALETTE = [
  "blue.6",
  "teal.6",
  "grape.6",
  "orange.6",
  "lime.6",
  "pink.6",
  "cyan.6",
  "yellow.6",
  "indigo.6",
  "red.6",
];

// Funnel stages are one metric decaying, so they share a hue and get lighter
// as the funnel narrows. Stages beyond the palette reuse the lightest shade.
const FUNNEL_SHADES = ["blue.8", "blue.7", "blue.6", "blue.5", "blue.4"];

const colorVar = (color) => `var(--mantine-color-${color.replace(".", "-")})`;

const formatShare = (value, total) =>
  total > 0 ? `${((value / total) * 100).toFixed(1)}%` : "—";

// Color swatch · label · value · share rows, used as the legend for donut and
// funnel charts so every number is readable without hovering.
const LegendTable = ({ rows }) => (
  <Stack gap={0} style={{ flex: "1 1 220px", minWidth: 0 }}>
    {rows.map((row) => (
      <Group
        key={row.key ?? row.label}
        gap="sm"
        wrap="nowrap"
        py={6}
        style={{ borderBottom: "1px solid var(--mantine-color-gray-3)" }}
      >
        <Box
          w={12}
          h={12}
          style={{
            borderRadius: 2,
            background: colorVar(row.color),
            flexShrink: 0,
          }}
        />
        <Text
          size="sm"
          style={{ flex: 1, minWidth: 0, wordBreak: "break-word" }}
        >
          {row.label}
        </Text>
        <Text size="sm" fw={700} style={{ whiteSpace: "nowrap" }}>
          {row.value.toLocaleString()}
        </Text>
        <Text
          size="sm"
          c="dimmed"
          ta="right"
          w={56}
          style={{ whiteSpace: "nowrap", flexShrink: 0 }}
        >
          {row.share}
        </Text>
      </Group>
    ))}
  </Stack>
);

export const ChartFrame = ({
  part,
  title,
  hasData,
  emptyMih = 160,
  headerRight,
  children,
}) =>
  usePartShown(part) ? (
    <Stack
      gap="xs"
      p="md"
      className="insights-chart-frame"
      style={{
        borderRadius: "var(--mantine-radius-sm)",
        border: "1px solid var(--mantine-color-gray-3)",
      }}
    >
      {(title || headerRight) && (
        <Group
          justify="space-between"
          align="flex-start"
          wrap="wrap"
          w="100%"
          className="insights-chart-header"
        >
          {title ? <Title order={5}>{title}</Title> : <span />}
          {headerRight}
        </Group>
      )}
      {hasData ? children : <EmptyState mih={emptyMih} />}
    </Stack>
  ) : null;

// Ordered funnel: every stage must be a subset of the one before it.
export const StageFunnel = ({ part, title, stages, size = 240 }) => {
  const rows = stages.map((stage, index) => ({
    ...stage,
    value: Number(stage.value) || 0,
    color: FUNNEL_SHADES[Math.min(index, FUNNEL_SHADES.length - 1)],
  }));
  const hasData = rows.some((row) => row.value > 0);
  const first = rows[0]?.value ?? 0;

  return (
    <ChartFrame part={part} title={title} hasData={hasData}>
      <Group align="center" gap="xl" wrap="wrap">
        <FunnelChart
          data={rows.map((row) => ({
            name: row.label,
            value: row.value,
            color: row.color,
          }))}
          size={size}
          withLabels
          labelsPosition="inside"
          valueFormatter={(value) => value.toLocaleString()}
        />
        <LegendTable
          rows={rows.map((row, index) => ({
            key: row.key,
            label: row.label,
            color: row.color,
            value: row.value,
            share: index > 0 && first > 0 ? formatShare(row.value, first) : "",
          }))}
        />
      </Group>
    </ChartFrame>
  );
};

const RankedBarTooltip = ({ label, payload }) => {
  if (!payload?.length) return null;
  const rowColor = payload[0]?.payload?.color;
  const adjusted = rowColor
    ? payload.map((item) => ({ ...item, color: rowColor }))
    : payload;
  return <ChartTooltip label={label} payload={adjusted} type="area" />;
};

const seriesLabelColorCssVar = (color) => {
  if (!color) return "currentColor";
  const [family, shade = "6"] = String(color).split(".");
  const strongerShade = Math.min(9, Math.max(6, Number(shade) + 3));
  return `var(--mantine-color-${family}-${strongerShade})`;
};

const RankedStackedBarValueLabel = ({ viewBox, value, series }) => {
  const parts = String(value ?? "")
    .split(/\s\|\s/)
    .filter((part) => part.length > 0);
  if (parts.length === 0) return null;
  const x = (viewBox?.x ?? 0) + (viewBox?.width ?? 0) + 8;
  const y = (viewBox?.y ?? 0) + (viewBox?.height ?? 0) / 2;
  return (
    <text
      x={x}
      y={y}
      textAnchor="start"
      dominantBaseline="middle"
      xmlSpace="preserve"
      fontSize={12}
    >
      {parts.flatMap((part, index) => [
        index > 0 ? (
          <tspan key={`sep-${index}`} fill="var(--mantine-color-gray-7)">
            {" | "}
          </tspan>
        ) : null,
        <tspan
          key={`part-${index}`}
          fill={seriesLabelColorCssVar(series?.[index]?.color)}
        >
          {part}
        </tspan>,
      ])}
    </text>
  );
};

const LABEL_FONT_SIZE = 12;
const LABEL_LINE_HEIGHT = LABEL_FONT_SIZE + 2;
const LABEL_CHAR_WIDTH = LABEL_FONT_SIZE * 0.55;
const LABEL_PADDING_X = 16;
const LABEL_MIN_AXIS_WIDTH = 80;
const ROW_PADDING_Y = 14;
// Fixed vertical chrome around the plotted bars (x-axis, and the legend when
// shown), added on top of the rows so the chart height fits its content instead
// of floating inside a fixed minimum.
const X_AXIS_HEIGHT = 36;
const LEGEND_HEIGHT = 30;
// Fixed bar thickness so every ranked bar reads the same across all charts,
// regardless of the chart's row height (stacked bars, wrapped labels, etc.).
const BAR_SIZE = 32;
// Uniform row band (the frame each bar sits in): the bar plus breathing room,
// the same across every chart. Only grows when a wrapped label needs more room.
const ROW_HEIGHT = BAR_SIZE + 4;

// chars → px
const widthForChars = (chars) => Math.ceil(chars * LABEL_CHAR_WIDTH);
// px → chars
const charsForWidth = (width) =>
  Math.max(8, Math.floor((width - LABEL_PADDING_X) / LABEL_CHAR_WIDTH));
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

// split text into lines that fit `maxPerLine` chars
const wrapLines = (text, maxPerLine) => {
  const words = String(text ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return [""];
  const lines = [];
  let current = "";
  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length <= maxPerLine) {
      current = candidate;
      continue;
    }
    if (current) lines.push(current);
    let rest = word;
    while (rest.length > maxPerLine) {
      lines.push(rest.slice(0, maxPerLine));
      rest = rest.slice(maxPerLine);
    }
    current = rest;
  }
  if (current) lines.push(current);
  return lines;
};

const RankedYAxisTick = ({ x, y, payload, width = 160 }) => {
  const value = String(payload?.value ?? "");
  const lines = wrapLines(value, charsForWidth(width));
  const startY = -((lines.length - 1) * LABEL_LINE_HEIGHT) / 2 + 4;
  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor="end" fill="currentColor" fontSize={LABEL_FONT_SIZE}>
        <title>{value}</title>
        {lines.map((line, i) => (
          <tspan key={i} x={-8} y={startY + i * LABEL_LINE_HEIGHT}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
};

// Recharts <Bar> props: a fixed maxBarSize so every bar has the same thickness
// across charts, plus optional click/hover interactions.
const buildBarProps = ({ onBarClick, onBarHover, categoryKey }) => {
  const props = { maxBarSize: BAR_SIZE };
  if (onBarClick) {
    props.style = { cursor: "pointer" };
    props.onClick = (entry, _index, event) => {
      event?.stopPropagation?.();
      onBarClick(entry?.[categoryKey]);
    };
  }
  if (onBarHover) {
    props.onMouseEnter = (entry) => onBarHover(entry?.[categoryKey]);
    props.onMouseLeave = () => onBarHover(null);
  }
  return props;
};

export const RankedBarChart = ({
  part,
  title,
  data,
  categoryKey,
  series,
  yAxisWidth = 280,
  limit = 10,
  withLegend,
  xAxisProps,
  onBarClick,
  onBarHover,
  headerRight,
}) => {
  const hasData = hasChartData(
    data,
    series.map((s) => s.name)
  );
  const showLegend = withLegend ?? series.length > 1;
  const seriesNames = series.map((s) => s.name);
  const lastSeriesName = seriesNames[seriesNames.length - 1];

  // Series sharing a stackId render as one stacked bar. Per-segment value
  // labels are misleading there (a segment's number doesn't line up with its
  // width) and bunch up unreadably, so instead of labelling each segment we
  // print one breakdown (`v1 | v2 | v3`, in legend order) to the right of the
  // whole bar. Single-series bars keep the default value label.
  const isStacked = series.length > 1 && series.every((s) => s.stackId);
  const baseRows = limit ? data.slice(0, limit) : data;
  const rows = isStacked
    ? baseRows.map((row) => ({
        ...row,
        __breakdown: seriesNames
          .map((n) => (Number(row[n]) || 0).toLocaleString())
          .join(" | "),
      }))
    : baseRows;
  const labels = rows.map((r) => String(r[categoryKey] ?? ""));

  const valueLabelProps = isStacked
    ? (item) =>
        item.name === lastSeriesName
          ? {
              dataKey: "__breakdown",
              position: "right",
              content: (props) => (
                <RankedStackedBarValueLabel {...props} series={series} />
              ),
            }
          : { content: () => null }
    : undefined;

  // Reserve right margin for the value label that sits past the bar end: a
  // single number for plain bars, the longer `v1 | v2 | v3` breakdown for
  // stacked ones (~6.5px per char at the printed label size). Kept tight so the
  // bars keep most of the width.
  const breakdownChars = isStacked
    ? Math.max(0, ...rows.map((r) => r.__breakdown.length))
    : 0;
  const rightMargin = isStacked
    ? clamp(Math.round(breakdownChars * 6) + 8, 44, 110)
    : 44;

  const longestLabel = Math.max(0, ...labels.map((l) => l.length));
  const axisWidth = clamp(
    widthForChars(longestLabel) + LABEL_PADDING_X,
    LABEL_MIN_AXIS_WIDTH,
    yAxisWidth
  );

  const charsPerLine = charsForWidth(axisWidth);
  const tallestLabelLines = Math.max(
    1,
    ...labels.map((l) => wrapLines(l, charsPerLine).length)
  );
  // Grouped (non-stacked) multi-series charts draw one bar per series side by
  // side in each row, so the row needs room for all of them; stacked/single
  // charts draw a single bar per row.
  const barsPerRow = isStacked ? 1 : series.length;
  const computedRowHeight = Math.max(
    barsPerRow * ROW_HEIGHT,
    tallestLabelLines * LABEL_LINE_HEIGHT + ROW_PADDING_Y
  );
  const barProps = buildBarProps({ onBarClick, onBarHover, categoryKey });
  const chartHeight =
    rows.length * computedRowHeight +
    X_AXIS_HEIGHT +
    (showLegend ? LEGEND_HEIGHT : 0);
  return (
    <ChartFrame
      part={part}
      title={title}
      hasData={hasData}
      headerRight={headerRight}
    >
      <BarChart
        h={chartHeight}
        data={rows}
        dataKey={categoryKey}
        orientation="vertical"
        yAxisProps={{
          width: axisWidth,
          interval: 0,
          tick: <RankedYAxisTick width={axisWidth} />,
        }}
        xAxisProps={xAxisProps}
        series={series}
        withTooltip
        tooltipProps={{ content: <RankedBarTooltip /> }}
        // Mantine only emits bar value-label text when a valueFormatter is set
        // (the label formatter is `(v) => valueFormatter?.(v)`, so without one
        // every label renders empty).
        valueFormatter={(value) => value.toLocaleString()}
        withBarValueLabel
        valueLabelProps={valueLabelProps}
        withLegend={showLegend}
        legendProps={{ itemSorter: null }}
        gridAxis="none"
        tickLine="x"
        barProps={barProps}
        // Right margin keeps the value label (single number, or the stacked
        // breakdown) from being clipped at the plot edge in the printed report.
        barChartProps={{ margin: { right: rightMargin } }}
      />
    </ChartFrame>
  );
};

export const DailyAreaChart = ({
  part,
  title,
  data,
  series,
  height = 220,
  withFill = true,
  curveType = "monotone",
  bucketSelector = false,
  defaultBucket = "3",
  withLegend = true,
  highlightedSeries = null,
  withPrintPointLabels = true,
  headerRight = null,
}) => {
  const [bucket, setBucket] = useState(defaultBucket);

  const displayData = useMemo(() => {
    if (!bucketSelector) return data ?? [];
    const hours = Number(bucket);
    const grouped = groupByHours(data ?? [], hours);
    return grouped.map((d) => ({ ...d, day: formatBucket(d.hour, hours) }));
  }, [data, bucket, bucketSelector]);

  // When a series is highlighted, fade the rest to gray and move the highlighted
  // one to the end so it draws on top of the faded lines (AreaChart renders
  // series in array order; later ones paint above earlier ones).
  const effectiveSeries = useMemo(() => {
    const focused = series.find((s) => s.name === highlightedSeries);
    if (!focused) return series;
    const faded = series
      .filter((s) => s !== focused)
      .map((s) => ({ ...s, color: "gray.3" }));
    return [...faded, focused];
  }, [series, highlightedSeries]);

  // Thicken the highlighted line and thin/fade the rest (and drop the faded
  // dots) so it stays readable even when many lines overlap in a dense chart.
  const areaProps = useMemo(() => {
    if (!highlightedSeries) return undefined;
    return (item) => {
      if (item.name === highlightedSeries) {
        return { strokeWidth: 3, strokeOpacity: 1 };
      }
      return { strokeWidth: 1, strokeOpacity: 0.45, dot: false };
    };
  }, [highlightedSeries]);

  const hasData = hasChartData(
    displayData,
    series.map((s) => s.name)
  );

  return (
    <ChartFrame
      part={part}
      title={title}
      hasData={hasData}
      headerRight={headerRight}
    >
      {bucketSelector && (
        <SegmentedControl
          className="insights-print-hide"
          size="xs"
          value={bucket}
          onChange={setBucket}
          data={BUCKET_OPTIONS}
          style={{ alignSelf: "flex-start" }}
        />
      )}
      <AreaChart
        h={height}
        data={displayData}
        dataKey="day"
        series={effectiveSeries}
        yAxisProps={{ allowDecimals: false }}
        curveType={curveType}
        withDots
        withLegend={withLegend}
        legendProps={{ itemSorter: null }}
        withTooltip
        // Point value labels are rendered into the DOM for the printed/PDF
        // report, but kept invisible on screen via insights.print.css so the
        // interactive chart stays uncluttered. Skipped on dense multi-line
        // charts where per-point labels would be unreadable.
        withPointLabels={withPrintPointLabels}
        fillOpacity={withFill ? undefined : 0}
        areaProps={areaProps}
      />
    </ChartFrame>
  );
};

export const ResponsesPieChart = ({ part, title, data, height = 168 }) => {
  const total = data.reduce((sum, d) => sum + Number(d.value ?? 0), 0);
  const colored = data
    .map((d, i) => ({
      name: d.name,
      value: Number(d.value ?? 0),
      color: PIE_PALETTE[i % PIE_PALETTE.length],
    }))
    .filter((d) => d.value > 0);

  return (
    <ChartFrame part={part} title={title} hasData={total > 0}>
      <Group align="center" gap="xl" wrap="wrap">
        <DonutChart
          size={height}
          h={height}
          thickness={Math.round(height / 7)}
          data={colored}
          withTooltip
          tooltipDataSource="segment"
          strokeWidth={1}
        />
        <LegendTable
          rows={colored.map((d) => ({
            label: d.name,
            color: d.color,
            value: d.value,
            share: formatShare(d.value, total),
          }))}
        />
      </Group>
    </ChartFrame>
  );
};

const groupTextAnswers = (answers) => {
  const map = new Map();
  for (const raw of answers ?? []) {
    if (raw == null) continue;
    const text = String(raw).trim();
    if (!text) continue;
    const key = text.toLowerCase();
    const prev = map.get(key);
    if (prev) prev.count += 1;
    else map.set(key, { text, count: 1 });
  }
  return Array.from(map.values()).sort((a, b) => b.count - a.count);
};

// Choice options keep their own order (and palette colour); text answers are
// grouped case-insensitively and ranked by how often they were given.
const toAnswerRows = ({ kind, options, answers }) =>
  kind === "text"
    ? groupTextAnswers(answers).map((g) => ({ label: g.text, count: g.count }))
    : (options ?? []).map((o, i) => ({
        label: o.label ?? o.title ?? "(unnamed)",
        count: Number(o.count) || 0,
        color: PIE_PALETTE[i % PIE_PALETTE.length],
      }));

// CSS conic-gradient ring: no measuring, so it renders inside a collapsed
// panel and prints exactly as drawn.
const MiniDonut = ({ rows, total, size = 112 }) => {
  let acc = 0;
  const stops = rows
    .filter((r) => r.count > 0)
    .map((r) => {
      const from = acc;
      acc += (r.count / total) * 100;
      return `${colorVar(r.color)} ${from.toFixed(2)}% ${acc.toFixed(2)}%`;
    })
    .join(", ");
  return (
    <Box pos="relative" w={size} h={size} style={{ flexShrink: 0 }}>
      <Box
        aria-hidden="true"
        w={size}
        h={size}
        style={{
          borderRadius: "50%",
          background: `conic-gradient(${stops})`,
          WebkitMask: "radial-gradient(circle, transparent 54%, #000 55%)",
          mask: "radial-gradient(circle, transparent 54%, #000 55%)",
        }}
      />
      <Box
        pos="absolute"
        inset={0}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Text fw={700} lh={1.1}>
          {total.toLocaleString()}
        </Text>
        <Text size="xs" c="dimmed" lh={1.1} className="insights-donut-caption">
          responses
        </Text>
      </Box>
    </Box>
  );
};

const QuestionRow = ({ item, isOpen, onToggle }) => {
  const rows = toAnswerRows(item);
  const total = rows.reduce((sum, r) => sum + r.count, 0);
  const maxCount = Math.max(0, ...rows.map((r) => r.count));
  const isText = item.kind === "text";
  const top = rows.find((r) => r.count === maxCount && r.count > 0) ?? null;
  const panelId = `insights-question-${item.id}`;
  const indent = item.isFollowUp ? 44 : 16;

  return (
    <div
      className="insights-question"
      data-open={isOpen || undefined}
      style={{
        background: item.isFollowUp ? "var(--mantine-color-gray-0)" : undefined,
      }}
    >
      <UnstyledButton
        className="insights-question-row"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={panelId}
        style={{ paddingLeft: indent }}
      >
        <Stack gap={2} style={{ minWidth: 0 }}>
          <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
            {item.isFollowUp && (
              <Badge variant="outline" color="grape" size="xs">
                Follow-up
              </Badge>
            )}
            <Text size="sm" fw={600} truncate>
              {item.title}
            </Text>
          </Group>
          <Text size="xs" c="dimmed" className="insights-question-meta">
            {item.meta}
          </Text>
        </Stack>
        {isText ? (
          <span className="insights-question-bar" />
        ) : (
          <Group
            gap={2}
            wrap="nowrap"
            h={10}
            className="insights-question-bar"
            style={{
              borderRadius: 5,
              overflow: "hidden",
              background: "var(--mantine-color-gray-2)",
            }}
          >
            {total > 0 &&
              rows
                .filter((r) => r.count > 0)
                .map((r) => (
                  <Box
                    key={r.label}
                    h="100%"
                    style={{
                      width: `${(r.count / total) * 100}%`,
                      background: colorVar(r.color),
                    }}
                  />
                ))}
          </Group>
        )}
        <Text size="sm" truncate className="insights-question-top">
          {!top
            ? "No responses yet"
            : isText
              ? `Top: ${top.label}`
              : `${top.label} · ${formatShare(top.count, total)}`}
        </Text>
        <Text
          size="xs"
          c="dimmed"
          className="insights-print-only insights-question-meta-print"
        >
          {item.meta}
        </Text>
        <IconChevronDown
          size={16}
          className="insights-question-chevron insights-print-hide"
          style={{
            color: "var(--mantine-color-dimmed)",
            transform: isOpen ? "rotate(180deg)" : undefined,
          }}
        />
      </UnstyledButton>
      <div
        id={panelId}
        className="insights-question-panel"
        style={{
          padding: `12px 16px 16px ${indent}px`,
          borderTop: "1px dashed var(--mantine-color-gray-3)",
        }}
      >
        {total === 0 ? (
          <Text size="sm" c="dimmed">
            No responses yet
          </Text>
        ) : (
          <Group align="flex-start" gap="xl" wrap="wrap">
            {!isText && <MiniDonut rows={rows} total={total} />}
            <div style={{ flex: "1 1 320px", minWidth: 0 }}>
              <div
                className="insights-answer-row insights-answer-head"
                data-text={isText || undefined}
              >
                <Text size="xs" c="dimmed" fw={600} tt="uppercase">
                  Answer
                </Text>
                {!isText && <span />}
                <Text size="xs" c="dimmed" fw={600} tt="uppercase" ta="right">
                  Responses
                </Text>
                <Text size="xs" c="dimmed" fw={600} tt="uppercase" ta="right">
                  Share
                </Text>
              </div>
              {rows.map((r) => (
                <div
                  key={r.label}
                  className="insights-answer-row"
                  data-text={isText || undefined}
                >
                  <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                    {!isText && (
                      <Box
                        w={10}
                        h={10}
                        style={{
                          borderRadius: 2,
                          flexShrink: 0,
                          background: colorVar(r.color),
                        }}
                      />
                    )}
                    <Text size="sm" style={{ wordBreak: "break-word" }}>
                      {r.label}
                    </Text>
                  </Group>
                  {!isText && (
                    <Box
                      h={8}
                      style={{
                        borderRadius: 4,
                        overflow: "hidden",
                        background: "var(--mantine-color-gray-1)",
                      }}
                    >
                      <Box
                        h="100%"
                        style={{
                          width: `${(r.count / maxCount) * 100}%`,
                          borderRadius: 4,
                          background: colorVar(r.color),
                        }}
                      />
                    </Box>
                  )}
                  <Text size="sm" fw={600} ta="right">
                    {r.count.toLocaleString()}
                  </Text>
                  <Text size="sm" c="dimmed" ta="right">
                    {formatShare(r.count, total)}
                  </Text>
                </div>
              ))}
            </div>
          </Group>
        )}
      </div>
    </div>
  );
};

// One compact row per question (title, a share bar of all answers and the top
// answer); clicking a row reveals its full breakdown. Every panel stays in the
// DOM so the printed report can show them all expanded.
export const QuestionList = ({ items }) => {
  const [openId, setOpenId] = useState(null);
  if (items.length === 0) return <EmptyState mih={80} />;
  return (
    <Box
      bg="var(--mantine-color-body)"
      style={{
        border: "1px solid var(--mantine-color-gray-3)",
        borderRadius: "var(--mantine-radius-sm)",
        overflow: "hidden",
      }}
    >
      {items.map((item) => (
        <QuestionRow
          key={item.id}
          item={item}
          isOpen={openId === item.id}
          onToggle={() =>
            setOpenId((prev) => (prev === item.id ? null : item.id))
          }
        />
      ))}
    </Box>
  );
};

export const formatDay = (day) => dayjs(day).format("MMM D");
